import React, { useEffect, useMemo, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import { Moment } from 'moment'

import { ERRORS } from 'consts'
import { useApiCall } from 'hooks'
import { snackbar } from 'components'

import {
  getParkingMetricsByBenefit,
  getParkingMetricsByBrand,
  getParkingMetricsByLevel,
  getParkingMetricsByTag,
} from './api'
import { stripHtml } from './format'
import { HoursDonutCard } from './HoursDonutCard'
import {
  ParkingBrandMetricRow,
  ParkingBenefitMetricRow,
  ParkingDonutSlice,
  ParkingLevelMetricRow,
  ParkingMetricsByBenefitResponse,
  ParkingMetricsByBrandResponse,
  ParkingMetricsByLevelResponse,
  ParkingMetricsByTagResponse,
  ParkingMetricsDateParams,
  ParkingTagMetricRow,
} from './types'

interface Props {
  startDate: Moment
  endDate: Moment
}

const formatPeriodLabel = (startDate: Moment, endDate: Moment) => {
  const start = startDate.clone().locale('es').format('MMMM YYYY')
  const end = endDate.clone().locale('es').format('MMMM YYYY')
  const capitalize = (value: string) =>
    value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value
  return `${capitalize(start)} - ${capitalize(end)}`
}

const toBenefitSlices = (rows: ParkingBenefitMetricRow[]): ParkingDonutSlice[] =>
  rows
    .filter(row => (row.consumedMinutes || 0) > 0)
    .map(row => ({
      name: stripHtml(row.title) || `Config ${row.parkingConfigId}`,
      value: row.consumedMinutes,
      percent: row.consumedPercent,
    }))

const toLevelSlices = (rows: ParkingLevelMetricRow[]): ParkingDonutSlice[] =>
  rows
    .filter(row => (row.consumedMinutes || 0) > 0)
    .map(row => ({
      name: `Nivel ${row.level}`,
      value: row.consumedMinutes,
      percent: row.consumedPercent,
    }))

const toNamedSlices = (
  rows: Array<ParkingTagMetricRow | ParkingBrandMetricRow>,
): ParkingDonutSlice[] =>
  rows
    .filter(row => (row.consumedMinutes || 0) > 0)
    .map(row => ({
      name: stripHtml(row.title) || 'Sin nombre',
      value: row.consumedMinutes,
      percent: row.consumedPercent,
    }))

const ParkingHoursCharts: React.FC<Props> = ({ startDate, endDate }) => {
  const [benefitSlices, setBenefitSlices] = useState<ParkingDonutSlice[]>([])
  const [levelSlices, setLevelSlices] = useState<ParkingDonutSlice[]>([])
  const [tagSlices, setTagSlices] = useState<ParkingDonutSlice[]>([])
  const [brandSlices, setBrandSlices] = useState<ParkingDonutSlice[]>([])

  const [requestByBenefit, loadingBenefit] = useApiCall<
    ParkingMetricsDateParams,
    ParkingMetricsByBenefitResponse
  >(getParkingMetricsByBenefit)
  const [requestByLevel, loadingLevel] = useApiCall<
    ParkingMetricsDateParams,
    ParkingMetricsByLevelResponse
  >(getParkingMetricsByLevel)
  const [requestByTag, loadingTag] = useApiCall<
    ParkingMetricsDateParams,
    ParkingMetricsByTagResponse
  >(getParkingMetricsByTag)
  const [requestByBrand, loadingBrand] = useApiCall<
    ParkingMetricsDateParams,
    ParkingMetricsByBrandResponse
  >(getParkingMetricsByBrand)

  useEffect(() => {
    const params = { startDate, endDate }
    const showError = () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)

    requestByBenefit(params)
      .then(resp => setBenefitSlices(toBenefitSlices(resp?.data || [])))
      .catch(() => {
        setBenefitSlices([])
        showError()
      })

    requestByLevel(params)
      .then(resp => setLevelSlices(toLevelSlices(resp?.data || [])))
      .catch(() => {
        setLevelSlices([])
        showError()
      })

    requestByTag(params)
      .then(resp => setTagSlices(toNamedSlices(resp?.data || [])))
      .catch(() => {
        setTagSlices([])
        showError()
      })

    requestByBrand(params)
      .then(resp => setBrandSlices(toNamedSlices(resp?.data || [])))
      .catch(() => {
        setBrandSlices([])
        showError()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  const periodLabel = useMemo(() => formatPeriodLabel(startDate, endDate), [startDate, endDate])

  const charts = useMemo(
    () => [
      {
        title: 'Distribución de horas por niveles',
        slices: levelSlices,
        isLoading: loadingLevel,
      },
      {
        title: 'Distribución de horas por etiqueta',
        slices: tagSlices,
        isLoading: loadingTag,
      },
      {
        title: 'Distribución de horas por marca',
        slices: brandSlices,
        isLoading: loadingBrand,
      },
      {
        title: 'Distribución de horas por beneficio',
        slices: benefitSlices,
        isLoading: loadingBenefit,
      },
    ],
    [
      levelSlices,
      tagSlices,
      brandSlices,
      benefitSlices,
      loadingLevel,
      loadingTag,
      loadingBrand,
      loadingBenefit,
    ],
  )

  return (
    <Grid container spacing={3} item xs={12}>
      {charts.map(chart => (
        <Grid item xs={12} md={6} key={chart.title}>
          <HoursDonutCard
            title={chart.title}
            periodLabel={periodLabel}
            slices={chart.slices}
            isLoading={chart.isLoading}
          />
        </Grid>
      ))}
    </Grid>
  )
}

export { ParkingHoursCharts }
